import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, Sun, Moon } from "lucide-react";
import { NAV } from "../../data";
import { scrollToId, NavLink, Magnetic } from "./primitives";
import { Button } from "../ui/button";
import Logo from "./Logo";

export default function Navbar({ dark, setDark }) {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (href) => { setOpen(false); scrollToId(href); };
  const goCareers = () => { setOpen(false); navigate("/careers"); };

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-0 inset-x-0 z-50 px-4 pt-4"
    >
      <div className={`mx-auto max-w-7xl flex items-center justify-between rounded-full px-4 md:px-6 h-16 transition-colors duration-500 ${scrolled ? "backdrop-blur-xl bg-background/70 border border-border/10 shadow-lg shadow-black/5" : "border border-transparent"}`}>
        <button data-testid="nav-logo" onClick={() => go("#top")} className="flex items-center gap-2">
          <Logo />
        </button>

        <nav className="hidden md:flex items-center gap-1">
          {NAV.map((n) => (
            <NavLink key={n.href} testId={`nav-${n.label.toLowerCase()}`} onClick={() => go(n.href)}>
              {n.label}
            </NavLink>
          ))}
          <NavLink testId="nav-careers" onClick={goCareers}>Careers</NavLink>
        </nav>

        <div className="flex items-center gap-2">
          <button
            data-testid="theme-toggle"
            onClick={() => setDark(!dark)}
            className="grid place-items-center h-10 w-10 rounded-full border border-border/15 text-foreground hover:border-primary transition-colors"
            aria-label="Toggle theme"
          >
            {dark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>
          <Magnetic strength={0.35}>
            <Button data-testid="nav-cta" onClick={() => go("#contact")} className="hidden sm:inline-flex rounded-full bg-brand-gradient text-white border-0 hover:opacity-90 shimmer-card">
              Get Free Consultation
            </Button>
          </Magnetic>
          <button data-testid="nav-mobile-toggle" onClick={() => setOpen(!open)} className="md:hidden grid place-items-center h-10 w-10 rounded-full border border-border/15">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -12 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -12 }}
            className="md:hidden mx-auto max-w-7xl mt-2 rounded-3xl backdrop-blur-xl bg-background/90 border border-border/10 p-4 shadow-xl"
          >
            {NAV.map((n) => (
              <button key={n.href} onClick={() => go(n.href)} className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium hover:bg-muted/40">
                {n.label}
              </button>
            ))}
            <button data-testid="nav-careers-mobile" onClick={goCareers} className="block w-full text-left px-4 py-3 rounded-xl text-base font-medium hover:bg-muted/40">
              Careers
            </button>
            <Button data-testid="nav-cta-mobile" onClick={() => go("#contact")} className="w-full mt-2 rounded-full bg-brand-gradient text-white border-0">
              Get Free Consultation
            </Button>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
